/**
 * Manager for material creation and management
 */

import {
  Texture as BabylonTexture,
  Color3,
  type Material,
  PBRMaterial,
  type Scene,
  StandardMaterial,
  type Texture,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type {
  MaterialConfig,
  PBRMaterialConfig,
  StandardMaterialConfig,
} from "./types";

/**
 * Manages material creation, configuration, and lifecycle
 */
export class MaterialManager extends BaseManager {
  private scene: Scene | null = null;
  private materials: Map<string, Material> = new Map();

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("materialManagerReady", {});
  }

  /**
   * Set the scene for material creation
   * @param scene The Babylon scene
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Create a material with the given configuration
   * @param config Material configuration
   */
  createMaterial(config: MaterialConfig): Material {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene first.");
    }

    if (this.materials.has(config.name)) {
      throw new Error(`Material with name ${config.name} already exists`);
    }

    let material: Material;

    switch (config.type) {
      case "standard":
        material = this.createStandardMaterial(config);
        break;
      case "pbr":
        material = this.createPBRMaterial(config);
        break;
      default:
        throw new Error(
          `Unsupported material type: ${(config as MaterialConfig).type}`,
        );
    }

    // Apply common properties
    if (config.backFaceCulling !== undefined) {
      material.backFaceCulling = config.backFaceCulling;
    }

    if (config.wireframe !== undefined) {
      material.wireframe = config.wireframe;
    }

    if (config.alpha !== undefined) {
      material.alpha = config.alpha;
    }

    // Store material
    this.materials.set(config.name, material);

    // Emit event
    this.emit("materialCreated", {
      material,
      name: config.name,
      type: config.type,
    });

    return material;
  }

  /**
   * Create a standard material
   * @param config Material configuration
   */
  private createStandardMaterial(
    config: StandardMaterialConfig,
  ): StandardMaterial {
    if (!this.scene) {
      throw new Error("Scene is not set");
    }
    const material = new StandardMaterial(config.name, this.scene);

    // Apply colors
    if (config.diffuseColor) {
      material.diffuseColor = this.createColor3(config.diffuseColor);
    }

    if (config.specularColor) {
      material.specularColor = this.createColor3(config.specularColor);
    }

    if (config.emissiveColor) {
      material.emissiveColor = this.createColor3(config.emissiveColor);
    }

    if (config.ambientColor) {
      material.ambientColor = this.createColor3(config.ambientColor);
    }

    // Apply textures
    if (config.diffuseTexture) {
      material.diffuseTexture = this.createTexture(config.diffuseTexture);
    }

    if (config.specularTexture) {
      material.specularTexture = this.createTexture(config.specularTexture);
    }

    if (config.emissiveTexture) {
      material.emissiveTexture = this.createTexture(config.emissiveTexture);
    }

    if (config.bumpTexture) {
      material.bumpTexture = this.createTexture(config.bumpTexture);
    }

    // Apply properties
    if (config.specularPower !== undefined) {
      material.specularPower = config.specularPower;
    }

    return material;
  }

  /**
   * Create a PBR material
   * @param config Material configuration
   */
  private createPBRMaterial(config: PBRMaterialConfig): PBRMaterial {
    if (!this.scene) {
      throw new Error("Scene is not set");
    }
    const material = new PBRMaterial(config.name, this.scene);

    // Apply colors
    if (config.albedoColor) {
      material.albedoColor = this.createColor3(config.albedoColor);
    }

    if (config.reflectivityColor) {
      material.reflectivityColor = this.createColor3(config.reflectivityColor);
    }

    if (config.emissiveColor) {
      material.emissiveColor = this.createColor3(config.emissiveColor);
    }

    // Apply textures
    if (config.albedoTexture) {
      material.albedoTexture = this.createTexture(config.albedoTexture);
    }

    if (config.metallicTexture) {
      material.metallicTexture = this.createTexture(config.metallicTexture);
    }

    if (config.normalTexture) {
      material.bumpTexture = this.createTexture(config.normalTexture);
    }

    if (config.emissiveTexture) {
      material.emissiveTexture = this.createTexture(config.emissiveTexture);
    }

    // Apply properties
    if (config.metallic !== undefined) {
      material.metallic = config.metallic;
    }

    if (config.roughness !== undefined) {
      material.roughness = config.roughness;
    }

    if (config.environmentIntensity !== undefined) {
      material.environmentIntensity = config.environmentIntensity;
    }

    return material;
  }

  /**
   * Create a Color3 from configuration
   * @param color Color configuration
   */
  private createColor3(color: { r: number; g: number; b: number }): Color3 {
    return new Color3(color.r, color.g, color.b);
  }

  /**
   * Create a texture from configuration
   * @param textureConfig Texture configuration
   */
  private createTexture(
    textureConfig: string | { url: string; uScale?: number; vScale?: number },
  ): Texture {
    if (!this.scene) {
      throw new Error("Scene is not set");
    }
    const url =
      typeof textureConfig === "string" ? textureConfig : textureConfig.url;
    const texture = new BabylonTexture(url, this.scene);

    if (typeof textureConfig === "object") {
      if (textureConfig.uScale !== undefined) {
        texture.uScale = textureConfig.uScale;
      }
      if (textureConfig.vScale !== undefined) {
        texture.vScale = textureConfig.vScale;
      }
    }

    return texture;
  }

  /**
   * Get a material by name
   * @param name Material name
   */
  getMaterial(name: string): Material | undefined {
    return this.materials.get(name);
  }

  /**
   * Get all materials
   */
  getAllMaterials(): Map<string, Material> {
    return new Map(this.materials);
  }

  /**
   * Remove a material
   * @param name Material name
   */
  removeMaterial(name: string): void {
    const material = this.materials.get(name);
    if (material) {
      material.dispose();
      this.materials.delete(name);

      this.emit("materialRemoved", { name });
    }
  }

  /**
   * Clear all materials
   */
  clearAll(): void {
    for (const material of this.materials.values()) {
      material.dispose();
    }
    this.materials.clear();
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.clearAll();
    this.scene = null;
  }
}
